import moment from 'moment-timezone';

export const isWorkingTime = (station, openFrom, closedFrom, isTwentyFourHour) => {
  if (isTwentyFourHour) return true;
  const [openTimeHours, openTimeMinutes] = openFrom.split(':');
  const [closeTimeHours, closeTimeMinutes] = closedFrom.split(':');
  const currentTime = moment().tz(`${station?.tzdb}`);
  const timeForOpen = moment()
    .set({
      h: openTimeHours,
      m: openTimeMinutes,
      s: 0,
    })
    .tz(`${station?.tzdb}`);

  const checkNextDay = openTimeHours > closeTimeHours ? 1 : 0;

  const timeForClose = moment()
    .set({
      h: closeTimeHours,
      m: closeTimeMinutes,
      s: 0,
    })
    .add(checkNextDay, 'd')
    .tz(`${station?.tzdb}`);

  const resultWorkTime = currentTime.isBefore(timeForOpen, 'h') || currentTime.isAfter(timeForClose, 'h');

  if (resultWorkTime) {
    return false;
  } else {
    return true;
  }
};

export const getDate = seconds => {
  const getSeconds = Number(seconds);
  const getHours = getSeconds / 3600;
  return new Date(getSeconds * 1000)
    .toISOString()
    .slice(11, 19)
    .match(/\d+/g)
    .map((elem, index) => {
      if (index === 0) {
        return (elem = Math.floor(getHours).toFixed());
      }
      return elem;
    });
};

export const plugTypeMaping = {
  TYPE_1: 'TYPE 1',
  TYPE_2: 'TYPE 2',
  GBT: 'GB/T',
  EURO_SOCKET: 'EURO',
  CCS1: 'CCS1',
  CCS2: 'CCS2',
  GBT_DC: 'GB/T DC',
  CHADEMO: 'CHADEMO',
  NACS: 'NACS',
  TYPE_2_NO_CABBLE: 'TYPE 2 без кабелю',
  undefined: '',
};

/** DC fast-charge connectors (aligned with admin-portal DC_PLUG_TYPES). */
const DC_PLUG_TYPES = new Set(['GBT_DC', 'CCS1', 'CCS2', 'CCS_2', 'CHADEMO', 'NACS']);
const PLUG_TYPES_WITHOUT_CONNECTED_STATUS = new Set(['CHADEMO', 'EURO_SOCKET']);

/** Station rated power above this (kW) is treated as DC for SOC UI. */
export const SOC_MIN_POWER_KW = 30;

export function isDcPlugType(plugType) {
  return DC_PLUG_TYPES.has(plugType);
}

/** These plugs have no reliable cable-plugged / Preparing state; UI must not show "connected". */
export function plugReportsConnectedStatus(plugType) {
  return !PLUG_TYPES_WITHOUT_CONNECTED_STATUS.has(String(plugType || '').toUpperCase());
}

export function isStationInService(stationOrName) {
  const name = typeof stationOrName === 'string' ? stationOrName : stationOrName?.name;
  return Boolean(name?.toLowerCase().includes('inservice'));
}

export const OCPP_DEFAULT_RESERVATION_MINUTES = 15;
export const DC_DEFAULT_RESERVATION_MINUTES = 20;
export const AC_DEFAULT_RESERVATION_MINUTES = 60;

function hasNameMarker(name, marker) {
  return new RegExp(`(?:^|\\s)${marker}(?:\\s|$)`, 'i').test(name || '');
}

export function isReservationProtocolEligible(station) {
  if (!station?.protocolType) {
    return false;
  }
  return station.protocolType === 'OCPP' || station.protocolType === 'MQTT';
}

export function isStationAllowReservation(stationOrName) {
  if (typeof stationOrName === 'string') {
    return false;
  }
  if (!isReservationProtocolEligible(stationOrName)) {
    return false;
  }
  const name = stationOrName?.name || '';
  if (hasNameMarker(name, 'noReservation')) {
    return false;
  }
  // reservationMinutes:0 — markers may stay, but StartPage hides the reserve button.
  if (getReservationMinutes(stationOrName) <= 0) {
    return false;
  }
  if (stationOrName.protocolType === 'OCPP') {
    return true;
  }
  return hasNameMarker(name, 'allowReservation');
}

export function getReservationMinutes(stationOrName) {
  const name = typeof stationOrName === 'string' ? stationOrName : stationOrName?.name;
  const match = (name || '').match(/reservationMinutes:(\d+)/i);
  if (match) {
    const n = parseInt(match[1], 10);
    return Math.min(120, Math.max(0, Number.isFinite(n) ? n : OCPP_DEFAULT_RESERVATION_MINUTES));
  }
  if (typeof stationOrName !== 'string' && stationOrName?.protocolType === 'OCPP') {
    return OCPP_DEFAULT_RESERVATION_MINUTES;
  }
  if (typeof stationOrName !== 'string' && isDcPlugType(stationOrName?.plugType)) {
    return DC_DEFAULT_RESERVATION_MINUTES;
  }
  if (typeof stationOrName !== 'string') {
    return AC_DEFAULT_RESERVATION_MINUTES;
  }
  return OCPP_DEFAULT_RESERVATION_MINUTES;
}

/** Next booking opens this long after lastJob.stoppedOn. */
export const POST_JOB_BOOK_COOLDOWN_MS = 10 * 60 * 1000;

const ACTIVE_JOB_STATES = new Set(['CREATED', 'WAITING', 'IN_PROGRESS']);

/**
 * Seconds left until booking is allowed after the previous job finished.
 * Active jobs do not start this cooldown. Returns 0 when booking is allowed.
 */
export function getPostJobBookCooldownSeconds(lastJob, nowMs = Date.now()) {
  if (!lastJob || ACTIVE_JOB_STATES.has(lastJob.state)) {
    return 0;
  }
  const stoppedAt = Date.parse(lastJob.stoppedOn);
  if (!Number.isFinite(stoppedAt)) {
    return 0;
  }
  const remainingMs = stoppedAt + POST_JOB_BOOK_COOLDOWN_MS - nowMs;
  if (remainingMs <= 0) {
    return 0;
  }
  return Math.ceil(remainingMs / 1000);
}
export const RESERVATION_WHITELIST_CLIENT_ID = 'skynet1';

export function isReservationWhitelistClient(clientUiId) {
  return clientUiId === RESERVATION_WHITELIST_CLIENT_ID;
}

export function getReservationErrorCode(err) {
  if (!err || typeof err !== 'object') {
    return null;
  }
  return err.data?.error ?? err.error ?? null;
}

export function formatReservationCountdown(totalSeconds) {
  const secs = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(secs / 60);
  const seconds = secs % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Charging remaining time: h:mm:ss when ≥1h, otherwise mm:ss. */
export function formatChargingCountdown(totalSeconds) {
  const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** dd.mm.yyyy from online_updated_on when port was marked offline / in service. */
export function formatInServiceSinceDdMm(onlineUpdatedOn) {
  if (!onlineUpdatedOn) {
    return null;
  }
  const date = new Date(onlineUpdatedOn);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  return `${dd}.${mm}.${yyyy}`;
}

export function getPortInServiceMessage(station, translate) {
  if (!isStationInService(station)) {
    return null;
  }
  const date = formatInServiceSinceDdMm(station?.onlineUpdatedOn || station?.online_updated_on);
  return date ? translate('portInServiceSince', { date }) : translate('portInService');
}

export function isStationInRepair(stationOrName) {
  const name = typeof stationOrName === 'string' ? stationOrName : stationOrName?.name;
  return /(?:^|\s)inRepair(?:\s|$)/i.test(name || '');
}

export function getInRepairSinceIsoDate(stationOrName) {
  const name = typeof stationOrName === 'string' ? stationOrName : stationOrName?.name;
  const match = (name || '').match(/inRepairSince:(\d{4}-\d{2}-\d{2})/i);
  if (match) {
    return match[1];
  }
  if (typeof stationOrName === 'string') {
    return null;
  }
  const onlineUpdatedOn = stationOrName?.onlineUpdatedOn || stationOrName?.online_updated_on;
  if (!onlineUpdatedOn) {
    return null;
  }
  const date = new Date(onlineUpdatedOn);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getInRepairDays(stationOrName) {
  const sinceIso = getInRepairSinceIsoDate(stationOrName);
  if (!sinceIso) {
    return 0;
  }
  const since = new Date(`${sinceIso}T00:00:00`);
  if (Number.isNaN(since.getTime())) {
    return 0;
  }
  const diffMs = Date.now() - since.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

/** Ukrainian plural form for day count (1 день, 2 дні, 5 днів). */
export function formatUaDaysCount(days) {
  const n = Math.max(0, Number(days) || 0);
  const lastDigit = n % 10;
  const lastTwoDigits = n % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${n} днів`;
  }
  if (lastDigit === 1) {
    return `${n} день`;
  }
  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${n} дні`;
  }
  return `${n} днів`;
}

export function getPortInRepairMessage(station, translate) {
  if (!isStationInRepair(station)) {
    return null;
  }
  const days = getInRepairDays(station);
  const daysText = formatUaDaysCount(days);
  const lang = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') || 'ua' : 'ua';
  if (lang === 'ua') {
    return `в ремонті ${daysText}`;
  }
  return translate('portInRepairForDays', { daysText });
}

/** AC NACS (Tesla destination / wall connector) typically rates below this. */
export const AC_NACS_MAX_KW = 10;

/** True for NACS ports below 10 kW — AC, not DC fast-charge. */
export function isAcNacsPort(plugType, maxPowerWt) {
  if (plugType !== 'NACS') {
    return false;
  }
  const ratedWatts = Number(maxPowerWt);
  return Number.isFinite(ratedWatts) && ratedWatts > 0 && ratedWatts < AC_NACS_MAX_KW * 1000;
}

/** DC port: CHAdeMO/CCS/etc. or station max power > 30 kW. AC NACS (< 10 kW) is excluded. */
export function isDcPort(plugType, maxPowerWt) {
  if (isAcNacsPort(plugType, maxPowerWt)) {
    return false;
  }
  if (isDcPlugType(plugType)) {
    return true;
  }
  const ratedWatts = Number(maxPowerWt);
  return Number.isFinite(ratedWatts) && ratedWatts > SOC_MIN_POWER_KW * 1000;
}

/** Offline message for /start; appends DC emergency-stop hint when applicable. */
export function getStationOfflineMessage(station, translate) {
  let message = translate('errorOfflineBody');
  if (isDcPort(station?.plugType, station?.maxPowerWt)) {
    message += `\n\n${translate('errorOfflineDcEmergencyHint')}`;
  }
  return message;
}

export function splitToCorrectNumber(number) {
  const newNumber = number.slice(3).split('');
  const correctNumber = newNumber.reduce((acc, elem, index) => {
    if (index === 3 || index === 6 || index === 8) {
      acc += '-';
    }
    acc += elem;
    return acc;
  }, '');
  return correctNumber;
}

/**
 * Format pasted single-line dynamic tariff notes into readable lines.
 * Aligns with admin-portal/dynamic_price.py tier syntax (day headers, "з 10:00 - 15.99 грн", Latin z).
 */
export function formatDynamicTariffNotes(text) {
  if (text == null || typeof text !== 'string') return text;
  const trimmed = text.trim().replace(/\r\n/g, '\n');
  if (!trimmed) return text;

  let s = trimmed;
  // Outage queue title before first tier
  s = s.replace(/(Черга відключень\s+[\d.]+)\s+(?=[\u0437zZ]\s+\d{1,2}:\d{2})/gi, '$1\n');
  // Weekday section headers (UA + EN)
  s = s.replace(/\s+(?=((?:пн-пт|сб-вс|сб-нд)|(?:mon-fri|sat-sun|weekdays?|weekend))\s+)/gi, '\n');
  // Time tiers: "з 10:00 - 15.99" (dash after time). Do NOT split DAM windows "з 10:00 до 15:00".
  s = s.replace(/\s+(?=[\u0437zZ]\s+\d{1,2}:\d{2}\s*[-–—])/g, '\n');
  // SoC dynamic price tiers: one parameter per line
  s = s.replace(/\s+(?=soc\s+до\s+\d+\s*%)/gi, '\n');
  s = s.replace(/(^|\n)soc(\s+до\s+\d+\s*%)/gi, '$1SoC$2');
  // DAM / РДН lines
  s = s.replace(/\s+(?=при\s+рдн\s+мен)/gi, '\n');
  s = s.replace(/\s+(?=\d+(?:[.,]\d+)?\s*[-–—]\s*тариф\s+(?:базовий|рдн))/gi, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s;
}

const TARIFF_TIER_LINE_RE = /^([\u0437zZ])\s+(\d{1,2}:\d{2})\s*[-–—]\s*(.+)$/;
const DAM_HEADER_RE = /^при\s+рдн\s+мен(?:ьше|ше)\s+(\d+(?:[.,]\d+)?)\s*грн\s*$/i;
const DAM_PRICE_LINE_RE = /^(\d+(?:[.,]\d+)?)\s*[-–—]\s*(тариф\s+(?:базовий|рдн).*)$/i;
/** Price + short label; optional windows stay in group 3 (shown once, not in the row). */
const DAM_PRICE_PARTS_RE = /^(\d+(?:[.,]\d+)?)\s*[-–—]\s*(тариф\s+(?:базовий|рдн))\s*(?:\(([^)]*)\))?\s*$/i;

/**
 * Split DAM suffix «з 00:00 до 06:00, …» / «завтра з 02:00 до 05:00» into chips.
 * @returns {{ labels: string[], isTomorrow: boolean, inactive: boolean }}
 */
export function parseDamWindowMeta(raw) {
  const empty = { labels: [], isTomorrow: false, inactive: false };
  if (raw == null || typeof raw !== 'string') return empty;
  let text = raw
    .trim()
    .replace(/^[()]+|[()]+$/g, '')
    .trim();
  if (!text) return empty;

  if (/^(зараз\s+не\s+діє|not\s+active|inactive)\s*$/i.test(text)) {
    return { labels: [], isTomorrow: false, inactive: true };
  }

  let isTomorrow = false;
  const tomorrowPrefix = text.match(/^(завтра|tomorrow)\s+/i);
  if (tomorrowPrefix) {
    isTomorrow = true;
    text = text.slice(tomorrowPrefix[0].length).trim();
  }

  const parts = text
    .split(/[,;]+/)
    .map(part =>
      part
        .trim()
        .replace(/^[()]+|[()]+$/g, '')
        .trim()
    )
    .filter(Boolean);
  const labels = parts.map(part => {
    const m = part.match(/^[\u0437zZ]\s*(\d{1,2}:\d{2})\s+до\s+(\d{1,2}:\d{2})\s*$/i);
    if (m) return `${m[1]}–${m[2]}`;
    return part.replace(/^[\u0437zZ]\s+/i, '');
  });
  return { labels, isTomorrow, inactive: false };
}

/** @deprecated use parseDamWindowMeta — kept for callers that only need label strings. */
export function parseDamWindowLabels(raw) {
  return parseDamWindowMeta(raw).labels;
}

const DAY_MINUTES = 24 * 60;

function parseClockMinutes(value) {
  const m = String(value).match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

function formatClockMinutes(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Turn DAM windows + base/DAM prices into a full-day list of «period: price» rows.
 * Gaps between cheap windows get the base price.
 * @returns {{ label: string, price: string, kind: 'base'|'dam' }[]}
 */
export function buildDamDriverPeriods({ tiers = [], damWindows = [], damWindowsInactive = false } = {}) {
  const basePrice = tiers.find(tier => tier.kind === 'base')?.timeLabel;
  const damPrice = tiers.find(tier => tier.kind === 'dam')?.timeLabel;

  const windows = damWindowsInactive
    ? []
    : damWindows
        .map(label => {
          const [from, to] = String(label).split(/\s*[-–—]\s*/);
          const start = parseClockMinutes(from);
          const end = parseClockMinutes(to);
          return start != null && end != null && end > start ? { start, end } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.start - b.start);

  if (!damPrice || !windows.length) {
    return basePrice ? [{ label: '00:00–24:00', price: basePrice, kind: 'base' }] : [];
  }

  const periods = [];
  const push = (start, end, kind) => {
    const price = kind === 'dam' ? damPrice : basePrice;
    if (!price || end <= start) return;
    const last = periods[periods.length - 1];
    if (last && last.kind === kind && last.end === start) {
      last.end = end;
      return;
    }
    periods.push({ start, end, kind, price });
  };

  let cursor = 0;
  for (const { start, end } of windows) {
    push(cursor, Math.max(cursor, start), 'base');
    push(Math.max(cursor, start), Math.min(end, DAY_MINUTES), 'dam');
    cursor = Math.max(cursor, Math.min(end, DAY_MINUTES));
  }
  push(cursor, DAY_MINUTES, 'base');

  return periods.map(({ start, end, kind, price }) => ({
    label: `${formatClockMinutes(start)}–${formatClockMinutes(end)}`,
    price,
    kind,
  }));
}

export const DAM_DRIVER_TITLE_UA = 'Тариф дешевший, коли низький РДН';
export const DAM_DRIVER_TITLE_EN = 'Cheaper tariff when DAM price is low';

/** Replace raw DAM rule header with driver-facing title; keep base/DAM price lines. */
export function formatDamNotesForDriver(text, preferUa = true) {
  if (text == null || typeof text !== 'string') return text;
  const formatted = formatDynamicTariffNotes(text);
  const lines = String(formatted || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.some(line => DAM_HEADER_RE.test(line))) {
    return formatted;
  }
  const title = preferUa ? DAM_DRIVER_TITLE_UA : DAM_DRIVER_TITLE_EN;
  const body = lines.filter(line => !DAM_HEADER_RE.test(line));
  return [title, ...body].join('\n');
}

/**
 * Split formatted dynamic-tariff notes into a title, free-text lines, and time/price rows.
 * DAM notes use driver title instead of «при РДН меньше …».
 */
export function splitDynamicTariffNotes(text, options = {}) {
  const preferUa = options.preferUa !== false;
  const formatted = formatDynamicTariffNotes(text);
  const lines = String(formatted || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return null;
  }

  const hasDam = lines.some(line => DAM_HEADER_RE.test(line) || DAM_PRICE_LINE_RE.test(line));
  if (hasDam) {
    const tiers = [];
    const otherLines = [];
    let damWindowMeta = { labels: [], isTomorrow: false, inactive: false };
    for (const line of lines) {
      if (DAM_HEADER_RE.test(line)) {
        continue;
      }
      const parts = line.match(DAM_PRICE_PARTS_RE);
      if (parts) {
        const kind = /рдн/i.test(parts[2]) ? 'dam' : 'base';
        if (kind === 'dam' && parts[3] && !damWindowMeta.labels.length && !damWindowMeta.inactive) {
          damWindowMeta = parseDamWindowMeta(parts[3]);
        }
        tiers.push({
          timeLabel: parts[1].replace(',', '.'),
          price: parts[2].trim(),
          kind,
        });
        continue;
      }
      const damMatch = line.match(DAM_PRICE_LINE_RE);
      if (damMatch) {
        const labelRaw = damMatch[2].trim();
        const winMatch = labelRaw.match(/^(тариф\s+(?:базовий|рдн))\s*(?:\(([^)]*)\))?\s*$/i);
        const label = winMatch ? winMatch[1].trim() : labelRaw.replace(/\s*\([^)]*\)\s*$/, '').trim();
        const kind = /рдн/i.test(label) ? 'dam' : 'base';
        if (kind === 'dam' && winMatch?.[2] && !damWindowMeta.labels.length && !damWindowMeta.inactive) {
          damWindowMeta = parseDamWindowMeta(winMatch[2]);
        }
        tiers.push({
          timeLabel: damMatch[1].replace(',', '.'),
          price: label,
          kind,
        });
      } else if (!TARIFF_TIER_LINE_RE.test(line)) {
        // Ignore orphaned DAM window fragments from older formatDynamicTariffNotes splits.
        if (/^(завтра|tomorrow)\b/i.test(line) || /^[\u0437zZ]\s*\d{1,2}:\d{2}\s+до\s+/i.test(line)) {
          if (!damWindowMeta.labels.length && !damWindowMeta.inactive) {
            damWindowMeta = parseDamWindowMeta(line);
          }
          continue;
        }
        otherLines.push(line);
      }
    }
    return {
      title: preferUa ? DAM_DRIVER_TITLE_UA : DAM_DRIVER_TITLE_EN,
      bodyLines: otherLines,
      tiers,
      damWindows: damWindowMeta.labels,
      damWindowsIsTomorrow: damWindowMeta.isTomorrow,
      damWindowsInactive: damWindowMeta.inactive,
      isDam: true,
    };
  }

  const tiers = [];
  const otherLines = [];
  for (const line of lines) {
    const match = line.match(TARIFF_TIER_LINE_RE);
    if (match) {
      tiers.push({
        timeLabel: `${match[1]} ${match[2]}`,
        price: match[3].trim(),
      });
    } else {
      otherLines.push(line);
    }
  }

  return {
    title: otherLines[0] || null,
    bodyLines: otherLines.slice(1),
    tiers,
    isDam: false,
  };
}

const DYNAMIC_TARIFF_TIER_RE = /[\u0437zZ]\s+\d{1,2}:\d{2}\s*[-–—]\s*[\d.]+\s*(?:грн|uah|₴)?/i;
const DAM_NOTES_RE = /при\s+рдн\s+мен(?:ьше|ше)|тариф\s+рдн/i;

/** Whether station notes contain a dynamic tariff schedule (outage queue, time tiers, DAM, etc.). */
export function isDynamicTariffNotes(text) {
  if (text == null || typeof text !== 'string' || !text.trim()) {
    return false;
  }
  return (
    DYNAMIC_TARIFF_TIER_RE.test(text) ||
    DAM_NOTES_RE.test(text) ||
    /Черга відключень/i.test(text) ||
    /динамічн/i.test(text) ||
    /dynamic\s+tariff/i.test(text)
  );
}

/** Parse dptime:… segment from station name into a readable schedule. */
export function parseDptimeScheduleFromStationName(name) {
  if (!name || !name.includes('dptime:')) {
    return null;
  }
  const match = name.match(/dptime:([^,]+(?:,[^,]+)*)/);
  if (!match) {
    return null;
  }
  const lines = match[1]
    .split(',')
    .map(part => {
      const [time, price] = part.split('_');
      if (!time || !price) {
        return null;
      }
      const [from] = time.split('-');
      return `з  ${from.padStart(2, '0')}:00 - ${parseInt(price, 10) / 100} грн`;
    })
    .filter(Boolean);
  if (!lines.length) {
    return null;
  }
  return `Динамічний тариф\n${lines.join('\n')}`;
}

/** Resolve formatted dynamic tariff schedule from station notes or dptime metadata. */
export function getDynamicTariffScheduleFromStation(station, lang = 'ua') {
  if (!station) {
    return null;
  }
  const langKey = (lang || 'ua').split('-')[0];
  const preferUa = langKey === 'ua' || langKey === 'uk';
  const noteCandidates = preferUa ? [station.notesUa, station.notesEn] : [station.notesEn, station.notesUa];

  for (const notes of noteCandidates) {
    if (isDynamicTariffNotes(notes)) {
      return formatDamNotesForDriver(notes.trim(), preferUa);
    }
  }

  return parseDptimeScheduleFromStationName(station.name);
}
